import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Loader2, Check, Info, ShieldCheck, TrendingDown, Globe } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useModules } from '@/contexts/ModulesContext';
import { getModuleIcon, MODULE_LANGS, moduleText } from '@/lib/moduleCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscription`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MyPlanPage() {
  const { refresh, gated } = useModules();
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState([]);
  const [savedKeys, setSavedKeys] = useState([]);
  const [interval, setIntervalState] = useState('month');
  const [savedInterval, setSavedInterval] = useState('month');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem('myplan_lang') || 'de');

  useEffect(() => { localStorage.setItem('myplan_lang', lang); }, [lang]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, auth());
      setModules(r.data.modules || []);
      const s = r.data.subscription || {};
      const keys = s.module_keys || [];
      setSelected(keys);
      setSavedKeys(keys);
      const iv = s.interval || 'month';
      setIntervalState(iv);
      setSavedInterval(iv);
    } catch {
      toast.error('Abo-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const total = useMemo(() => {
    const field = interval === 'year' ? 'yearly_chf' : 'monthly_chf';
    return modules.filter((m) => selected.includes(m.key)).reduce((s, m) => s + Number(m[field] || 0), 0);
  }, [modules, selected, interval]);

  const yearlySavings = useMemo(() => {
    return modules
      .filter((m) => selected.includes(m.key))
      .reduce((s, m) => s + (Number(m.monthly_chf || 0) * 12 - Number(m.yearly_chf || 0)), 0);
  }, [modules, selected]);

  const dirty = useMemo(() => {
    const a = [...selected].sort().join(',');
    const b = [...savedKeys].sort().join(',');
    return a !== b || interval !== savedInterval;
  }, [selected, savedKeys, interval, savedInterval]);

  const confirmApply = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/apply`, { module_keys: selected, interval }, auth());
      toast.success('Abo aktualisiert – es wurde nichts belastet.');
      setConfirmOpen(false);
      await load();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Änderung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500" data-testid="myplan-loading">Lädt…</div>;

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-6" data-testid="myplan-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mein Abo</h1>
        <p className="mt-1 text-sm text-gray-500">
          Wähle die Module, die du nutzen möchtest. Du siehst sofort den Preis und bestätigst deine Auswahl.
        </p>
      </div>

      {/* Hinweis: noch keine Belastung */}
      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="myplan-no-charge-note">
        <Info className="mt-0.5 h-5 w-5 shrink-0" />
        <span>
          <strong>Es erfolgt noch keine Belastung.</strong> Deine Auswahl wird sofort freigeschaltet. Die
          automatische Abrechnung wird später aktiviert – bis dahin dient der Preis nur zur Übersicht.
        </span>
      </div>

      {gated === false && (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="myplan-full-access-note">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          <span>Dein Konto hat aktuell <strong>alle Module freigeschaltet</strong> (Sonderfreigabe). Die Auswahl unten bestimmt dein reguläres Abo.</span>
        </div>
      )}

      {/* Intervall + Sprache */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-1 w-max" data-testid="myplan-interval">
          <button onClick={() => setIntervalState('month')} className={`rounded-lg px-4 py-2 text-sm font-medium ${interval === 'month' ? 'bg-blue-600 text-white' : 'text-gray-600'}`} data-testid="myplan-interval-month">Monatlich</button>
          <button onClick={() => setIntervalState('year')} className={`rounded-lg px-4 py-2 text-sm font-medium ${interval === 'year' ? 'bg-blue-600 text-white' : 'text-gray-600'}`} data-testid="myplan-interval-year">Jährlich</button>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1 w-max" data-testid="myplan-lang">
          <Globe className="ml-1 h-4 w-4 text-gray-400" />
          {MODULE_LANGS.map((l) => (
            <button key={l.code} onClick={() => setLang(l.code)} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${lang === l.code ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid={`myplan-lang-${l.code}`}>{l.label}</button>
          ))}
        </div>
        {yearlySavings > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200" data-testid="myplan-yearly-savings-badge">
            <TrendingDown className="h-3.5 w-3.5" />
            Mit Jahresabo {chf(yearlySavings)} / Jahr sparen
          </span>
        )}
      </div>

      {/* Modul-Schalter */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="myplan-modules">
        {modules.map((m) => {
          const on = selected.includes(m.key);
          const price = interval === 'year' ? m.yearly_chf : m.monthly_chf;
          const Ico = getModuleIcon(m.icon);
          const t = moduleText(m, lang);
          return (
            <div key={m.key} data-testid={`myplan-module-${m.key}`}
              className={`flex items-start justify-between rounded-2xl border p-4 transition-colors ${on ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg ${on ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  <Ico className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{m.name}</div>
                  <div className="mt-0.5 text-xs text-gray-500">{t.description}</div>
                  {t.benefits.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid={`myplan-benefits-${m.key}`}>
                      {t.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 text-xs font-medium text-gray-600">{chf(price)} / {interval === 'year' ? 'Jahr' : 'Monat'}</div>
                </div>
              </div>
              <Switch checked={on} onCheckedChange={() => toggle(m.key)} data-testid={`myplan-switch-${m.key}`} />
            </div>
          );
        })}
      </div>

      {/* Summe + Bestätigen */}
      <div className="sticky bottom-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-lg">
        <div>
          <div className="text-xs text-gray-500">Gesamt ({selected.length} Module, {interval === 'year' ? 'jährlich' : 'monatlich'})</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="myplan-total">{chf(total)}<span className="text-sm font-normal text-gray-400"> / {interval === 'year' ? 'Jahr' : 'Monat'}</span></div>
          {interval === 'year' && yearlySavings > 0 && (
            <div className="mt-0.5 text-xs font-medium text-emerald-600" data-testid="myplan-savings-footer">
              Du sparst {chf(yearlySavings)} pro Jahr gegenüber monatlicher Zahlung
            </div>
          )}
        </div>
        <Button onClick={() => setConfirmOpen(true)} disabled={!dirty || busy} className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50" data-testid="myplan-confirm-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Änderungen bestätigen
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="myplan-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Auswahl bestätigen</AlertDialogTitle>
            <AlertDialogDescription>
              Du schaltest <strong>{selected.length} Modul(e)</strong> frei. Neuer Preis:{' '}
              <strong>{chf(total)}</strong> / {interval === 'year' ? 'Jahr' : 'Monat'}.
              <br />
              <span className="text-amber-700">Es wird aktuell nichts belastet.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="myplan-confirm-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmApply(); }} disabled={busy} data-testid="myplan-confirm-apply">
              {busy ? 'Speichert…' : 'Bestätigen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
